import {
  getActiveAccountId,
  setStoredActorInviteAccountId,
} from "./account-context";
import { buildCssVariablesFromColors } from "@trustvid/design-system";

const DARK_APP_CANVAS_FALLBACK = "#101828";

const THEME_STORAGE_PREFIX = "tv:account:theme:";
const THEME_MODE_STORAGE_PREFIX = "tv:account:theme-mode:";

/** Default palette when the app is in light mode. */
const DEFAULT_LIGHT_COLORS = Object.freeze({
  primary: "#6f6ad9",
  secondary: "#9d9892",
  tertiary: "#616161",
  background: "#ffffff",
  text: "#111827",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
});

/** Default palette when the app is in dark mode (independent from light until the user edits). */
const DEFAULT_DARK_COLORS = Object.freeze({
  primary: "#6f6ad9",
  secondary: "#94a3b8",
  tertiary: "#64748b",
  background: "#0f172a",
  text: "#e2e8f0",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
});

export const DEFAULT_ACCOUNT_THEME = Object.freeze({
  mode: "light",
  themeModeDefault: "light",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  layout: {
    headerStyle: "neutral",
    sidebarStyle: "neutral",
  },
  logos: {
    light: "",
    dark: "",
    icon: "",
  },
  media: {
    favicon: "",
    hero: "",
  },
  colorsLight: DEFAULT_LIGHT_COLORS,
  colorsDark: DEFAULT_DARK_COLORS,
  /** @deprecated Use colorsLight / colorsDark. Alias of colorsLight for backward compatibility. */
  colors: DEFAULT_LIGHT_COLORS,
});

const COLOR_KEYS = Object.freeze(["primary", "secondary", "tertiary", "background", "text", "success", "warning", "error"]);
const HEX_COLOR_REGEX = /^#([0-9A-F]{6})$/i;
const THEME_MODES = Object.freeze(["light", "dark"]);
const LAYOUT_HEADER_STYLES = Object.freeze(["neutral", "primary"]);
const LAYOUT_SIDEBAR_STYLES = Object.freeze(["neutral", "primaryTint"]);

export const normalizeBrandingLayout = (layout) => {
  const raw = layout && typeof layout === "object" ? layout : {};
  return {
    headerStyle: LAYOUT_HEADER_STYLES.includes(raw.headerStyle) ? raw.headerStyle : "neutral",
    sidebarStyle: LAYOUT_SIDEBAR_STYLES.includes(raw.sidebarStyle) ? raw.sidebarStyle : "neutral",
  };
};

const cloneDefaultTheme = () => ({
  mode: DEFAULT_ACCOUNT_THEME.mode,
  themeModeDefault: DEFAULT_ACCOUNT_THEME.themeModeDefault,
  fontFamily: DEFAULT_ACCOUNT_THEME.fontFamily,
  layout: { ...DEFAULT_ACCOUNT_THEME.layout },
  logos: { ...DEFAULT_ACCOUNT_THEME.logos },
  media: { ...DEFAULT_ACCOUNT_THEME.media },
  colorsLight: { ...DEFAULT_ACCOUNT_THEME.colorsLight },
  colorsDark: { ...DEFAULT_ACCOUNT_THEME.colorsDark },
});

const normalizeMode = (value, fallback = "light") => {
  return THEME_MODES.includes(value) ? value : fallback;
};

const normalizeHex = (value) => {
  const normalized = String(value || "").trim();
  return HEX_COLOR_REGEX.test(normalized) ? normalized.toLowerCase() : null;
};

const sanitizeColors = (inputColors = {}, fallbackDefaults = DEFAULT_LIGHT_COLORS) => {
  const sanitized = {};
  COLOR_KEYS.forEach((key) => {
    sanitized[key] = normalizeHex(inputColors[key]) || fallbackDefaults[key];
  });
  return sanitized;
};

const migrateThemeColors = (theme) => {
  const hasLight = theme?.colorsLight != null && typeof theme.colorsLight === "object";
  const hasDark = theme?.colorsDark != null && typeof theme.colorsDark === "object";
  const hasLegacy = theme?.colors != null && typeof theme.colors === "object";

  if (!hasLight && !hasDark && hasLegacy) {
    const base = sanitizeColors(theme.colors, DEFAULT_LIGHT_COLORS);
    return { light: { ...base }, dark: { ...base } };
  }

  const light = sanitizeColors(
    hasLight ? theme.colorsLight : hasLegacy ? theme.colors : {},
    DEFAULT_LIGHT_COLORS,
  );

  const dark = sanitizeColors(
    hasDark ? theme.colorsDark : hasLight ? DEFAULT_DARK_COLORS : hasLegacy ? theme.colors : DEFAULT_DARK_COLORS,
    DEFAULT_DARK_COLORS,
  );

  return { light, dark };
};

export const resolveThemeStorageKey = (accountId) => `${THEME_STORAGE_PREFIX}${String(accountId || "default")}`;
export const resolveThemeModeStorageKey = (accountId) => `${THEME_MODE_STORAGE_PREFIX}${String(accountId || "default")}`;

export const normalizeAccountTheme = (theme) => {
  const fallbackMode = normalizeMode(theme?.themeModeDefault, DEFAULT_ACCOUNT_THEME.themeModeDefault);
  const selectedMode = normalizeMode(theme?.mode, fallbackMode);
  const { light: colorsLight, dark: colorsDark } = migrateThemeColors(theme);
  const logos = theme?.logos || {};
  const media = theme?.media || {};
  const fsRaw = theme?.font_scale ?? theme?.fontScale;
  let fontScale = undefined;
  if (fsRaw != null) {
    const n = Number(fsRaw);
    if (!Number.isNaN(n) && n >= 0.75 && n <= 1.75) {
      fontScale = n;
    }
  }
  const contrast =
    theme?.contrast === "high" || theme?.contrast === "normal" ? theme.contrast : undefined;

  const colors = selectedMode === "dark" ? colorsDark : colorsLight;

  return {
    mode: selectedMode,
    themeModeDefault: fallbackMode,
    fontFamily: String(theme?.fontFamily || DEFAULT_ACCOUNT_THEME.fontFamily),
    layout: normalizeBrandingLayout(theme?.layout),
    logos: {
      light: String(logos.light || ""),
      dark: String(logos.dark || ""),
      icon: String(logos.icon || ""),
    },
    media: {
      favicon: String(media.favicon || ""),
      hero: String(media.hero || ""),
    },
    colorsLight,
    colorsDark,
    colors,
    ...(fontScale !== undefined ? { fontScale } : {}),
    ...(contrast ? { contrast } : {}),
  };
};

/**
 * Optional per-user overlay (from `users.theme_preferences`) merged on top of a normalized account theme.
 * Supported keys: `mode`, `fontFamily`, `font_scale` | `fontScale`, `contrast` ('high' | 'normal').
 */
export const mergeUserThemePreferences = (normalizedTheme, prefs) => {
  const base = normalizeAccountTheme(normalizedTheme);
  if (prefs == null || typeof prefs !== "object" || Array.isArray(prefs)) {
    return base;
  }
  const out = {
    mode: base.mode,
    themeModeDefault: base.themeModeDefault,
    fontFamily: base.fontFamily,
    layout: { ...base.layout },
    logos: { ...base.logos },
    media: { ...base.media },
    colorsLight: { ...base.colorsLight },
    colorsDark: { ...base.colorsDark },
  };
  if (base.fontScale !== undefined) {
    out.fontScale = base.fontScale;
  }
  if (base.contrast) {
    out.contrast = base.contrast;
  }
  const mode = prefs.mode;
  if (mode && THEME_MODES.includes(mode)) {
    out.mode = mode;
  }
  if (typeof prefs.fontFamily === "string" && prefs.fontFamily.trim()) {
    out.fontFamily = prefs.fontFamily.trim();
  }
  const fs = prefs.font_scale ?? prefs.fontScale;
  if (fs != null) {
    const n = Number(fs);
    if (!Number.isNaN(n) && n >= 0.75 && n <= 1.75) {
      out.fontScale = n;
    }
  }
  if (prefs.contrast === "high" || prefs.contrast === "normal") {
    out.contrast = prefs.contrast;
  }
  return normalizeAccountTheme(out);
};

export const getPersistedThemeMode = (accountId) => {
  if (!accountId) {
    return null;
  }
  return normalizeMode(localStorage.getItem(resolveThemeModeStorageKey(accountId)), null);
};

export const savePersistedThemeMode = (accountId, mode) => {
  if (!accountId) {
    return null;
  }
  const normalized = normalizeMode(mode, null);
  if (!normalized) {
    localStorage.removeItem(resolveThemeModeStorageKey(accountId));
    return null;
  }
  localStorage.setItem(resolveThemeModeStorageKey(accountId), normalized);
  return normalized;
};

const themeToStoragePayload = (normalized) => {
  const payload = {
    mode: normalized.mode,
    themeModeDefault: normalized.themeModeDefault,
    fontFamily: normalized.fontFamily,
    layout: normalized.layout,
    logos: normalized.logos,
    media: normalized.media,
    colorsLight: normalized.colorsLight,
    colorsDark: normalized.colorsDark,
  };
  if (normalized.fontScale !== undefined) {
    payload.fontScale = normalized.fontScale;
  }
  if (normalized.contrast) {
    payload.contrast = normalized.contrast;
  }
  return payload;
};

export const getAccountTheme = (accountId) => {
  const fallback = normalizeAccountTheme(cloneDefaultTheme());
  if (!accountId) {
    return fallback;
  }

  const stored = localStorage.getItem(resolveThemeStorageKey(accountId));
  if (!stored) {
    const persistedMode = getPersistedThemeMode(accountId);
    if (persistedMode) {
      return normalizeAccountTheme({ ...fallback, mode: persistedMode });
    }
    return fallback;
  }

  try {
    const normalized = normalizeAccountTheme(JSON.parse(stored));
    const persistedMode = getPersistedThemeMode(accountId);
    if (persistedMode) {
      normalized.mode = persistedMode;
      normalized.colors = persistedMode === "dark" ? normalized.colorsDark : normalized.colorsLight;
    }
    return normalized;
  } catch (error) {
    console.error("Failed to parse account branding theme:", error);
    return fallback;
  }
};

export const ACCOUNT_BRANDING_UPDATED_EVENT = "tv:account-branding-updated";

export const saveAccountTheme = (accountId, theme) => {
  const normalized = normalizeAccountTheme(theme);
  if (!accountId) {
    return normalized;
  }
  localStorage.setItem(resolveThemeStorageKey(accountId), JSON.stringify(themeToStoragePayload(normalized)));
  savePersistedThemeMode(accountId, normalized.mode);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ACCOUNT_BRANDING_UPDATED_EVENT, {
        detail: { accountId: String(accountId), theme: normalized },
      })
    );
  }
  return normalized;
};

const SKIP_THEME_FETCH_PREFIX = "tv:skip-theme-fetch:";

export const markSkipThemeFetchOnce = (accountId) => {
  if (!accountId) return;
  try {
    localStorage.setItem(`${SKIP_THEME_FETCH_PREFIX}${accountId}`, "1");
  } catch {
    /* ignore */
  }
};

export const consumeSkipThemeFetch = (accountId) => {
  if (!accountId) return false;
  try {
    const key = `${SKIP_THEME_FETCH_PREFIX}${accountId}`;
    if (localStorage.getItem(key) === "1") {
      localStorage.removeItem(key);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

/** Persist and apply theme from actor invite preview / OTP / campaign-details. */
export const seedActorAccountThemeFromInvite = (account) => {
  const accountId = account?.id;
  if (!accountId) {
    return null;
  }

  setStoredActorInviteAccountId(accountId);

  const raw = account?.branding_theme;
  const next = raw != null ? saveAccountTheme(accountId, raw) : getAccountTheme(accountId);
  applyAccountTheme(next);
  markSkipThemeFetchOnce(accountId);
  return next;
};

/** Persist theme from login/switch payload so the shell matches the server before Branding page loads. */
export const seedAccountThemeFromLoginUser = (user) => {
  const accountId = user?.account?.id;
  if (!accountId) {
    return null;
  }
  const raw = user?.account?.branding_theme;
  if (raw == null) {
    return null;
  }
  const next = saveAccountTheme(accountId, raw);
  const merged = mergeUserThemePreferences(next, user?.theme_preferences);
  applyAccountTheme(merged);
  markSkipThemeFetchOnce(accountId);
  return merged;
};

export const clearAccountTheme = (accountId) => {
  if (!accountId) {
    return;
  }
  localStorage.removeItem(resolveThemeStorageKey(accountId));
  localStorage.removeItem(resolveThemeModeStorageKey(accountId));
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) {
    return null;
  }
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const hexToRgbCsv = (hex) => {
  const rgb = hexToRgb(hex);
  return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : null;
};

const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0"))
    .join("")}`;

const mixHex = (hexA, hexB, t) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) {
    return hexA;
  }
  const r = a.r + (b.r - a.r) * t;
  const g = a.g + (b.g - a.g) * t;
  const bl = a.b + (b.b - a.b) * t;
  return rgbToHex(r, g, bl);
};

const relativeLuminance = ({ r, g, b }) => {
  const lin = [r, g, b].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};

const isLightHexBackground = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return true;
  }
  return relativeLuminance(rgb) > 0.65;
};

/** Set only for light mode + dark background; must be cleared on reset/default theme. */
const CONDITIONAL_LAYOUT_CSS_VARIABLES = Object.freeze(["--bs-card-bg", "--bs-footer-bg"]);

/**
 * Maps account tokens to Bootstrap / Skote layout surfaces. Skote uses `var(--bs-secondary-bg)` for
 * `.card` backgrounds (`$card-bg`), so we must set secondary/tertiary explicitly — not only body-bg.
 */
const buildBrandedLayoutCssVariables = (colors, mode, fontFamily, layout = DEFAULT_ACCOUNT_THEME.layout) => {
  const layoutNorm = normalizeBrandingLayout(layout);
  const isDark = mode === "dark";
  const lightSurface = isLightHexBackground(colors.background);
  const darkCanvasInLightMode = !isDark && !lightSurface;

  let bodyBg;
  let secondaryBg;
  let tertiaryBg;

  if (isDark) {
    if (lightSurface) {
      bodyBg = DARK_APP_CANVAS_FALLBACK;
      secondaryBg = mixHex(DARK_APP_CANVAS_FALLBACK, colors.background, 0.14);
      tertiaryBg = mixHex(DARK_APP_CANVAS_FALLBACK, "#ffffff", 0.1);
    } else {
      bodyBg = colors.background;
      secondaryBg = mixHex(colors.background, "#ffffff", 0.08);
      tertiaryBg = mixHex(colors.background, "#ffffff", 0.12);
    }
  } else if (darkCanvasInLightMode) {
    bodyBg = colors.background;
    secondaryBg = mixHex(colors.background, "#ffffff", 0.08);
    tertiaryBg = mixHex(colors.background, "#ffffff", 0.14);
  } else {
    bodyBg = colors.background;
    secondaryBg = mixHex(colors.background, "#000000", 0.035);
    tertiaryBg = mixHex(colors.background, "#000000", 0.065);
  }

  let headerBg = isDark
    ? lightSurface
      ? mixHex(DARK_APP_CANVAS_FALLBACK, "#000000", 0.2)
      : mixHex(colors.background, "#000000", 0.35)
    : mixHex(colors.background, "#000000", 0.02);

  if (layoutNorm.headerStyle === "primary") {
    headerBg = colors.primary;
  }

  let headerItemColor = colors.text;
  if (layoutNorm.headerStyle === "primary" && !isLightHexBackground(colors.primary)) {
    headerItemColor = "#ffffff";
  }

  // Dark mode: keep the rail on-brand (never force the user's light "background" token onto the sidebar).
  let sidebarBg = isDark ? mixHex(bodyBg, colors.primary, 0.12) : colors.background;
  if (!isDark && layoutNorm.sidebarStyle === "primaryTint") {
    sidebarBg = mixHex(colors.background, colors.primary, 0.08);
  }

  const sidebarOnLight = !isDark || isLightHexBackground(sidebarBg);
  const sidebarMenu = sidebarOnLight ? colors.text : mixHex(colors.text, "#ffffff", 0.88);
  const sidebarIcon = sidebarOnLight
    ? mixHex(sidebarMenu, "#000000", 0.15)
    : mixHex(sidebarMenu, "#ffffff", 0.2);
  const sidebarSub = sidebarOnLight ? mixHex(colors.text, "#000000", 0.25) : mixHex(colors.text, "#ffffff", 0.65);
  const sidebarMenuHoverBg = sidebarOnLight
    ? mixHex(sidebarBg, colors.primary, 0.1)
    : mixHex(sidebarBg, "#ffffff", 0.1);

  const border = mixHex(secondaryBg, isDark ? "#ffffff" : "#000000", 0.14);
  const borderStrong = mixHex(border, isDark ? "#ffffff" : "#000000", 0.25);
  const borderHover = mixHex(border, isDark ? "#ffffff" : "#000000", 0.4);
  const textMuted = isDark
    ? mixHex(colors.text, "#ffffff", 0.35)
    : mixHex(colors.text, "#000000", 0.35);
  const textSubtle = isDark
    ? mixHex(colors.text, "#ffffff", 0.55)
    : mixHex(colors.text, "#000000", 0.45);
  const surface = isDark ? tertiaryBg : colors.background;
  const surfaceMuted = isDark ? bodyBg : secondaryBg;

  const secondaryBgRgb = hexToRgbCsv(secondaryBg);
  const tertiaryBgRgb = hexToRgbCsv(tertiaryBg);

  const layoutVars = {
    "--bs-body-bg": bodyBg,
    "--bs-secondary-bg": secondaryBg,
    "--bs-tertiary-bg": tertiaryBg,
    ...(secondaryBgRgb ? { "--bs-secondary-bg-rgb": secondaryBgRgb } : {}),
    ...(tertiaryBgRgb ? { "--bs-tertiary-bg-rgb": tertiaryBgRgb } : {}),
    "--bs-border-color": border,
    "--bs-heading-color": colors.text,
    "--bs-modal-color": colors.text,
    "--bs-modal-bg": surface,
    "--bs-header-bg": headerBg,
    "--bs-header-item-color": headerItemColor,
    "--bs-body-font-family": fontFamily,
    "--tv-font-family-base": fontFamily,
    "--tv-layout-sidebar-bg": sidebarBg,
    "--tv-layout-sidebar-menu-color": sidebarMenu,
    "--tv-layout-sidebar-icon-color": sidebarIcon,
    "--tv-layout-sidebar-menu-hover-color": colors.primary,
    "--tv-layout-sidebar-menu-hover-bg": sidebarMenuHoverBg,
    "--tv-layout-sidebar-menu-active-color": colors.primary,
    "--tv-layout-sidebar-submenu-color": sidebarSub,
    "--bs-emphasis-color": colors.text,
    "--bs-secondary-color": textMuted,
    "--tv-theme-surface": surface,
    "--tv-theme-surface-muted": surfaceMuted,
    "--tv-theme-text-muted": textMuted,
    "--tv-theme-text-subtle": textSubtle,
    "--tv-theme-border": border,
    "--tv-theme-border-strong": borderStrong,
    "--tv-theme-border-hover": borderHover,
    ...(isDark ? { "--bs-card-bg": surface } : {}),
  };

  if (!darkCanvasInLightMode) {
    return layoutVars;
  }

  const bodyRgb = hexToRgb(bodyBg);
  const bodyBgRgb = bodyRgb ? `${bodyRgb.r}, ${bodyRgb.g}, ${bodyRgb.b}` : null;

  return {
    ...layoutVars,
    "--bs-card-bg": secondaryBg,
    "--bs-footer-bg": secondaryBg,
    ...(bodyBgRgb ? { "--bs-body-bg-rgb": bodyBgRgb } : {}),
  };
};

const applyBrandingMediaToDocument = (normalized) => {
  if (typeof document === "undefined") {
    return;
  }
  const fav = normalized?.media?.favicon?.trim();
  if (!fav) {
    return;
  }
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = fav;
};

const brandingLogoCacheBust = (normalized) => {
  const raw = `${normalized.mode}|${normalized.logos.light}|${normalized.logos.dark}|${normalized.logos.icon}`;
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) {
    h = (h * 31 + raw.charCodeAt(i)) | 0;
  }
  return String(h);
};

/** Resolved logo URLs for shell UI (sidebar, header). Empty string means “use default / API fallback”. */
export const resolveBrandedLogoDisplayUrls = (branding) => {
  const normalized = normalizeAccountTheme(branding || {});
  const v = brandingLogoCacheBust(normalized);
  const withV = (url) => {
    const u = String(url || "").trim();
    if (!u) {
      return "";
    }
    const sep = u.includes("?") ? "&" : "?";
    return `${u}${sep}tvb=${v}`;
  };
  const { logos, mode } = normalized;
  const modeLogo = mode === "dark" ? logos.dark : logos.light;
  const main = String(modeLogo || logos.icon || "").trim();
  return {
    iconSrc: withV(logos.icon),
    mainSrc: withV(main),
  };
};

/** Avatar URL for header/profile: branding icon, then mode-specific main logo. */
export const resolveAccountAvatarDisplayUrl = (branding, accountLogo = "") => {
  const normalized = normalizeAccountTheme(branding || {});
  const { iconSrc, mainSrc } = resolveBrandedLogoDisplayUrls(normalized);
  if (iconSrc || mainSrc) {
    return iconSrc || mainSrc;
  }
  const { light, dark, icon } = normalized.logos;
  const hasAnyBrandingLogo = Boolean(light?.trim() || dark?.trim() || icon?.trim());
  if (hasAnyBrandingLogo) {
    return "";
  }
  return String(accountLogo || "").trim();
};

/** Account switcher row / chip avatar: branding theme first, then account settings logo. */
export const resolveAccountDropdownAvatarUrl = (account, { activeAccountId, activeBranding } = {}) => {
  if (!account?.id) {
    return "";
  }
  const isActive = String(account.id) === String(activeAccountId);
  const themeSource = isActive && activeBranding ? activeBranding : getAccountTheme(account.id);
  const normalized = normalizeAccountTheme(themeSource);
  const avatarFromBranding = resolveAccountAvatarDisplayUrl(normalized);
  if (avatarFromBranding) {
    return avatarFromBranding;
  }
  const hasBrandingLogo = Boolean(
    normalized.logos.light?.trim() || normalized.logos.dark?.trim() || normalized.logos.icon?.trim()
  );
  if (hasBrandingLogo) {
    return "";
  }
  return String(account?.logo || "").trim();
};

export const applyAccountTheme = (theme, target = document.documentElement) => {
  if (!target) {
    return;
  }
  const normalized = normalizeAccountTheme(theme);
  const { mode } = normalized;
  const palette = mode === "dark" ? normalized.colorsDark : normalized.colorsLight;

  const cssVariables = buildCssVariablesFromColors(palette);
  Object.entries(cssVariables).forEach(([key, value]) => {
    target.style.setProperty(key, value);
  });

  const layoutVars = buildBrandedLayoutCssVariables(palette, mode, normalized.fontFamily, normalized.layout);
  Object.entries(layoutVars).forEach(([key, value]) => {
    if (value != null && value !== "") {
      target.style.setProperty(key, value);
    }
  });
  CONDITIONAL_LAYOUT_CSS_VARIABLES.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(layoutVars, key)) {
      target.style.removeProperty(key);
    }
  });

  // Keep Bootstrap controls aligned with design-system defaults globally.
  target.style.setProperty("--bs-btn-border-radius", "var(--tv-radius-md)");
  target.style.setProperty("--bs-btn-border-radius-sm", "var(--tv-radius-sm)");
  target.style.setProperty("--bs-btn-border-radius-lg", "var(--tv-radius-lg)");
  target.style.setProperty("--bs-btn-padding-y", "0.47rem");
  target.style.setProperty("--bs-btn-padding-x", "0.9rem");
  target.style.setProperty("--bs-btn-font-weight", "500");
  target.style.setProperty("--bs-btn-border-width", "1px");
  target.style.setProperty("--bs-font-sans-serif", "var(--tv-font-family-base)");
  target.style.setProperty("--tv-theme-font-family", normalized.fontFamily);

  target.dataset.bsTheme = mode;

  if (normalized.fontScale != null && normalized.fontScale !== 1) {
    target.style.setProperty("--tv-user-font-scale", String(normalized.fontScale));
  } else {
    target.style.removeProperty("--tv-user-font-scale");
  }
  if (normalized.contrast === "high") {
    target.dataset.tvContrast = "high";
  } else {
    delete target.dataset.tvContrast;
  }

  applyBrandingMediaToDocument(normalized);
};

export const applyAccountThemeFromPathname = (pathname, preferredMode = null) => {
  const accountId = getActiveAccountId(pathname);
  const baseTheme = getAccountTheme(accountId);
  const theme = preferredMode ? { ...baseTheme, mode: normalizeMode(preferredMode, baseTheme.mode) } : baseTheme;
  applyAccountTheme(theme);
  if (accountId) {
    savePersistedThemeMode(accountId, theme.mode);
  }
  return theme;
};

export const accountThemeColorKeys = COLOR_KEYS;
